import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  validateHubStudentSettings,
  validateHubStudentSettingsAsync,
} from 'components/AppService/HubSettings/Student/ReservationSettings/validation';
import { cloneDeep, get, keyBy, omit, set } from 'lodash';
import { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import { HubStudentConfiguration } from 'types/hubStudentSettings.type';
import { IDefaultReducerState } from 'types/state.interface';
import { errorNotification } from 'utils/notifications';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { z } from 'zod';
import api from '../services/api.service';

export type THubStudentSettingsState = IDefaultReducerState & {
  config: Partial<HubStudentConfiguration> | null;
  errors: Record<string, z.ZodIssue> | null;
};

export const initialState: THubStudentSettingsState = {
  hasErrors: false,
  loading: false,
  config: {},
  errors: null,
};

const slice = createSlice({
  name: 'hubStudentSettings',
  initialState,
  reducers: {
    fetchSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchSettingsSuccess: (
      state,
      { payload }: PayloadAction<Partial<HubStudentConfiguration> | null>
    ) => {
      finishedLoadingSuccess(state);
      if (payload != null) {
        state.config = payload;
      }
    },
    fetchSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setConfig: (
      state,
      { payload }: PayloadAction<HubStudentConfiguration | null>
    ) => {
      state.config = payload;
    },
    hubStudentSubscribeSettingOnChange: (
      state,
      { payload }: PayloadAction<{ path: string; value: string | boolean }>
    ) => {
      if (!state.config) {
        state.config = {};
      }
      if (!state.config.studentSubscribeSettings) {
        state.config.studentSubscribeSettings = { enabled: false };
      }
      set(
        state.config.studentSubscribeSettings,
        `${payload.path}`,
        payload.value
      );
    },
    hubStudentReservationSettingOnChange: (
      state,
      { payload }: PayloadAction<{ path: string; value: any }>
    ) => {
      if (!state.config) {
        state.config = {};
      }
      if (!state.config.studentReservationDisplaySettings) {
        state.config.studentReservationDisplaySettings = {};
      }
      set(
        state.config.studentReservationDisplaySettings,
        `${payload.path}`,
        payload.value
      );
    },
    hubStudentReservationSettingOnClear: (
      state,
      { payload }: PayloadAction<{ path: string }>
    ) => {
      if (!state.config) {
        state.config = {};
      }
      if (!state.config.studentReservationDisplaySettings) {
        state.config.studentReservationDisplaySettings = {};
      }
      let newConfig = omit(state.config.studentReservationDisplaySettings, [
        payload.path,
      ]);
      const parentPath = payload.path.split('.').slice(0, -1).join('.');

      if (Object.keys(get(newConfig, parentPath) ?? {}).length === 0) {
        newConfig = omit(newConfig, [parentPath]);
      }

      state.config.studentReservationDisplaySettings = newConfig;
    },
    setErrors: (
      state,
      { payload }: PayloadAction<Record<string, z.ZodIssue> | null>
    ) => {
      if (payload === null) {
        state.hasErrors = false;
        state.errors = null;
      } else {
        state.hasErrors = true;
        state.errors = payload;
      }
    },
  },
});

export const {
  hubStudentReservationSettingOnChange,
  hubStudentReservationSettingOnClear,
  hubStudentSubscribeSettingOnChange,
} = slice.actions;

/// Selectors
export const hubStudentSettingsSelector = (state: TRootState) =>
  state.hubStudentSettings.config;

export const hubStudentSettingsLoadingSelector = (state: TRootState) =>
  state.hubStudentSettings.loading;

export const hubStudentSettingsErrorsSelector = (state: TRootState) =>
  state.hubStudentSettings.errors;

/// Thunks

export const fetchHubStudentSettings = createAppAsyncThunk(
  'hubSettings/hubStudentSettingsFetchSettings',
  async (_, { dispatch }) => {
    try {
      const result = await api.get({
        endpoint: `/apps/TE_HUB/student-reservation-display-settings`,
      });
      dispatch(slice.actions.fetchSettingsSuccess(configFromNetwork(result)));
    } catch (e) {
      console.error(e);
    }
  }
);

export const hubStudentSettingsHandleValidationFailure = createAppAsyncThunk(
  'hubSettings/hubStudentSettingsHandleValidationFailure',
  (
    {
      validationResult,
      sendNotification,
    }: { validationResult: any; sendNotification: boolean },
    { dispatch }
  ) => {
    const errorMap = keyBy(validationResult.error.issues, 'path');
    dispatch(slice.actions.setErrors(errorMap));
    if (sendNotification) {
      errorNotification(
        'Configuration could not be validated',
        ['Please verify fields marked in red'].concat(
          validationResult.error.issues.map((error) => error.message)
        )
      );
    }
    return;
  }
);

export const saveHubStudentSettings = createAppAsyncThunk(
  'hubSettings/saveHubStudentSettings',
  async (_, { getState, dispatch }) => {
    const config = getState().hubStudentSettings.config;

    const validationResult = validateHubStudentSettings(config);

    if (!validationResult.success) {
      dispatch(
        hubStudentSettingsHandleValidationFailure({
          validationResult,
          sendNotification: true,
        })
      );
      return;
    } else {
      dispatch(slice.actions.setErrors(null));
    }

    try {
      const result = await api.patch({
        endpoint: `/apps/TE_HUB/student-reservation-display-settings`,
        data: validationResult.data,
      });
      dispatch(slice.actions.fetchSettingsSuccess(configFromNetwork(result)));
    } catch (e) {
      console.error(e);
    }
  }
);

export const hubStudentSettingsValidate = createAppAsyncThunk(
  'hubSettings/hubStudentSettingsValidate',
  async (sendNotification: boolean, { dispatch, getState }) => {
    const config = getState().hubStudentSettings.config;
    const validationResult = await validateHubStudentSettingsAsync(config);

    if (!validationResult.success) {
      dispatch(
        hubStudentSettingsHandleValidationFailure({
          validationResult,
          sendNotification,
        })
      );
      return;
    } else {
      dispatch(slice.actions.setErrors(null));
    }
  }
);

const configFromNetwork = (config: Partial<HubStudentConfiguration> | null) => {
  const copy = cloneDeep(config);

  copy?.studentReservationDisplaySettings?.extraFields?.forEach((field) => {
    if (field._id) {
      field.id = field._id;
    }
  });

  copy?.studentReservationDisplaySettings?.location?.extraFields?.forEach(
    (field) => {
      if (field._id) {
        field.id = field._id;
      }
    }
  );

  return copy;
};

export default slice.reducer;
